import { createStart, createCsrfMiddleware, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";

// Keep the auth SDK out of the public landing bundle. The client is loaded
// only when an actual server-function request needs a bearer token.
const attachLazyAuth = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    const { getSupabase } = await import("@/lib/supabase-lazy");
    const supabase = await getSupabase();
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return next({ headers: token ? { Authorization: `Bearer ${token}` } : {} });
  },
);

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// On the Vercel-hosted public site there is no service key: forward server
// function calls to the Lovable backend (same source → same function ids).
const proxyServerFnsWithoutServiceKey = createMiddleware().server(async ({ request, next }) => {
  const url = new URL(request.url);
  if (!url.pathname.includes("/_serverFn/") || process.env["SUPABASE_SERVICE_ROLE_KEY"]) return next();
  const target = `https://panel.retaillemon.com${url.pathname}${url.search}`;
  if (url.host === "panel.retaillemon.com") return next();
  const headers = new Headers(request.headers);
  headers.delete("host");
  headers.delete("cookie");
  headers.set("origin", "https://panel.retaillemon.com");
  headers.set("referer", "https://panel.retaillemon.com/");
  const ip = request.headers.get("x-forwarded-for") ?? request.headers.get("x-real-ip");
  if (ip) headers.set("x-forwarded-for", ip);
  try {
    const body = request.method === "GET" || request.method === "HEAD" ? undefined : await request.arrayBuffer();
    const res = await fetch(target, { method: request.method, headers, body: body ?? null });
    if (!res.ok) console.error(`[serverFn-proxy] ${url.pathname} → ${res.status}`);
    // fetch() ya descomprime el cuerpo: no reenviar content-encoding/length o
    // Safari intenta descomprimir texto plano y falla con "Load failed".
    const outHeaders = new Headers(res.headers);
    outHeaders.delete("content-encoding");
    outHeaders.delete("content-length");
    outHeaders.delete("transfer-encoding");
    outHeaders.delete("set-cookie");
    return new Response(res.body, { status: res.status, headers: outHeaders });
  } catch (err) {
    console.error("[serverFn-proxy] missing SUPABASE_SERVICE_ROLE_KEY and panel unreachable:", String(err));
    throw err;
  }
});

// Start installs this automatically when src/start.ts is absent; defining the
// file opts out, so re-add it explicitly to keep server functions protected
// from cross-site requests.
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

export const startInstance = createStart(() => ({
  // attachSupabaseAuth se sustituye a propósito por attachLazyAuth: el cliente
  // de backend se carga solo cuando una server function necesita el token.
  functionMiddleware: [attachLazyAuth],
  requestMiddleware: [errorMiddleware, csrfMiddleware, proxyServerFnsWithoutServiceKey],
}));
